import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { consoleGrouped, consoleLog } from "src/app/support/console";
import { tryParseJson } from "src/app/support/json";
import { SseEvents } from "src/app/sse/sse-events";

export type ServerEventsStatus = "connecting" | "connected" | "not-connected";

class SSEAuthExpiredError extends Error {
  constructor() {
    super("SSEAuthExpiredError");
  }
}
class SSERetriableError extends Error {
  constructor() {
    super("SSERetriableError");
  }
}

@Injectable({ providedIn: "root" })
export class ServerEventsService {
  static instance: ServerEventsService | undefined;

  status$: BehaviorSubject<ServerEventsStatus> =
    new BehaviorSubject<ServerEventsStatus>("not-connected");

  message$: Subject<void> = new Subject();

  readonly #listernesMap = new Map<string, EventListener[]>();

  #abortController: AbortController | undefined;

  constructor() {
    ServerEventsService.instance = this;
  }

  async start({
    headersFactory,
    endpointFactory
  }: {
    headersFactory: () => Promise<Record<string, string>>;
    endpointFactory: () => Promise<string>;
  }): Promise<void> {
    const headers: Record<string, string> = await headersFactory();
    const endpoint: string = await endpointFactory();

    this.#abortController = new AbortController();

    await new Promise<void>((res, rej) => {
      fetchEventSource(endpoint, {
        headers,
        signal: this.#abortController?.signal,
        openWhenHidden: true,
        onopen: async (response) => {
          consoleLog("[SSE] onopen");

          switch (true) {
            case response.ok &&
              response.headers.get("content-type") === "text/event-stream": {
              consoleLog("[SSE] onopen success");
              this.status$.next("connected");

              res();
              return; // everything's good
            }
            case response.status === 401: {
              consoleLog("[SSE] onopen unauthorized");
              // if the server closes the connection unexpectedly, retry:
              throw new SSEAuthExpiredError();
            }
            default: {
              consoleLog("[SSE] onopen retry");
              throw new SSERetriableError();
            }
          }
        },
        onmessage: (msg) => {
          consoleGrouped(`[SSE] onmessage "${msg.event}"`, () => ({
            ...msg,
            data: tryParseJson(msg.data, {})
          }));

          this.publish(<any>msg);
        },
        onclose: () => {
          this.status$.next("not-connected");
          console.log(`[SSE] onclose`);

          throw new SSERetriableError();
        },
        onerror: (err) => {
          console.warn(`[SSE] onerror ${err}`);

          this.status$.next("connecting");

          if (err instanceof SSEAuthExpiredError) {
            throw err;
          }

          return 2000;
        }
      }).catch((error) => {
        if (error instanceof SSEAuthExpiredError) {
          this.status$.next("connecting");

          return this.start({
            headersFactory,
            endpointFactory
          });
        } else {
          rej(error);
          throw error;
        }
      });
    });
  }

  async stop(): Promise<void> {
    this.#abortController?.abort();
    this.status$.next("not-connected");
  }

  addListener(type: string, listener: EventListener, silent = false): void {
    this.#listernesMap.set(type, [
      ...(this.#listernesMap.get(type) || []),
      listener
    ]);
  }

  removeListener(type: string, listener: EventListener): void {
    this.#listernesMap.set(type, [
      ...(this.#listernesMap.get(type) || []).filter((x) => x !== listener)
    ]);
  }

  publish(msg: { event: SseEvents; data?: any; isOptmistic: boolean }): void {
    this.message$.next();

    if (this.#listernesMap.has(msg.event)) {
      this.#listernesMap
        .get(msg.event)
        ?.forEach((listener) =>
          listener(getMessagePayloadWithoutEventProperty(msg))
        );
    }
  }
}

function getMessagePayloadWithoutEventProperty(msg: any): any {
  const { event, ...rest } = msg;
  return {
    ...rest,
    type: event
  };
}
